import {
  CoinBalances,
  CoinDetails,
  DepositInfo,
  Transactions,
  TransactionType,
} from './types';
import baseApiPs2 from '../baseApiPs2';
import { injectEndpoints } from 'apis/util';
import { fixSearchParams } from '@zignaly-open/ui';

export const api = injectEndpoints(baseApiPs2, (builder) => ({
  coins: builder.query<
    CoinBalances,
    { exchangeInternalId: string; convert?: boolean }
  >({
    query: ({ exchangeInternalId, convert = false }) => ({
      url: `user/exchanges/${exchangeInternalId}/assets`,
      params: {
        view: 'reduced',
        convert: convert,
      },
    }),
    providesTags: ['CoinBalance'],
    extraOptions: {
      silent: true,
    },
  }),

  allCoins: builder.query<CoinDetails, string>({
    query: (exchangeType) => ({
      url: `coins/zgly_${exchangeType}`,
    }),
  }),

  depositInfo: builder.query<
    DepositInfo,
    { exchangeId: string; networkId: string; coinId: string }
  >({
    query: ({ exchangeId, coinId, networkId }) => ({
      url: `/user/exchanges/${exchangeId}/deposit_address/${coinId}?network=${networkId}`,
    }),
  }),

  withdraw: builder.mutation<
    { id: string },
    {
      exchangeInternalId: string;
      network: string;
      asset: string;
      tag: string;
      address: string;
      amount: string;
      code: string;
      code2FA?: string;
    }
  >({
    query: ({ exchangeInternalId, ...rest }) => ({
      url: `/user/exchanges/${exchangeInternalId}/withdraw`,
      method: 'POST',
      body: rest,
    }),
    // invalidateTags delayed to allow for the withdrawal to be processed
    async onQueryStarted(_, { dispatch, queryFulfilled }) {
      await queryFulfilled;
      setTimeout(() => {
        dispatch(api.util.invalidateTags(['CoinBalance']));
      }, 10000);
    },
  }),

  transactionsHistory: builder.query<
    Transactions,
    {
      exchangeInternalId: string;
      type: string;
      from?: string;
      limit?: number;
    }
  >({
    query: ({ exchangeInternalId, ...params }) => ({
      url: `user/exchanges/${exchangeInternalId}/transactions_history`,
      params: fixSearchParams(params),
    }),
  }),

  transactionsHistoryCsv: builder.mutation<
    { id: string },
    {
      exchangeInternalId: string;
      days: number;
      type?: TransactionType;
    }
  >({
    query: ({ exchangeInternalId, days, type }) => ({
      url: `/user/exchanges/${exchangeInternalId}/transactions_history_csv`,
      method: 'POST',
      params: {
        days,
        ...(type && { type }),
      },
    }),
  }),
  quoteAssetsCoin: builder.query<
    Array<string>,
    {
      exchangeInternalId: string;
      coinId: string;
    }
  >({
    query: ({ exchangeInternalId, coinId }) => ({
      url: `quote_assets/${exchangeInternalId}/${coinId}`,
    }),
  }),

  gasCheck: builder.query<{ ok: boolean }, void>({
    query: () => ({
      url: `user/gas_check`,
      method: 'GET',
    }),
    keepUnusedDataFor: 0,
  }),
}));

export const {
  useCoinsQuery,
  useLazyCoinsQuery,
  useGasCheckQuery,
  useAllCoinsQuery,
  useDepositInfoQuery,
  useWithdrawMutation,
  useTransactionsHistoryCsvMutation,
  useTransactionsHistoryQuery,
  useQuoteAssetsCoinQuery,
} = api;
